import PropTypes from 'prop-types';
import { useMemo } from 'react';

// material-ui
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';

// project imports
import DrawerHeader from './DrawerHeader';
import DrawerContent from './DrawerContent';
import DrawerFooter from './DrawerFooter';
import MiniDrawerStyled from './MiniDrawerStyled';
import IconButton from 'components/@extended/IconButton';

import { DRAWER_WIDTH } from 'config';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// assets
import { IconChevronLeft, IconChevronRight } from 'utils/icons';

// ==============================|| MAIN LAYOUT - DRAWER ||============================== //

export default function MainDrawer({ window }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  // responsive drawer container
  const container = window !== undefined ? () => window().document.body : undefined;

  // header content
  const drawerContent = useMemo(() => <DrawerContent />, []);
  const drawerHeader = useMemo(() => <DrawerHeader open={drawerOpen} />, [drawerOpen]);
  const drawerFooter = useMemo(() => <DrawerFooter open={drawerOpen} />, [drawerOpen]);

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 }, zIndex: 1200 }} aria-label="mailbox folders">
      {!downLG ? (
        <Box sx={{ position: 'relative' }}>
          <MiniDrawerStyled variant="permanent" open={drawerOpen}>
            {drawerHeader}
            {drawerContent}
            {drawerFooter}
          </MiniDrawerStyled>
          <IconButton
            aria-label={drawerOpen ? 'zavřít menu' : 'otevřít menu'}
            onClick={() => handlerDrawerOpen(!drawerOpen)}
            color="secondary"
            variant="outlined"
            sx={{
              // top: 14 vycentruje 32px tlačítko přesně na výšku navbaru
              // (60px Toolbar) - pevná hodnota, ne 50% (rodičovský Box má
              // nulovou výšku, takže 50% by bylo zavádějící základ).
              position: 'absolute',
              top: 14,
              right: -21,
              zIndex: 1201,
              width: 32,
              height: 32,
              minWidth: 32,
              bgcolor: 'background.paper',
              borderColor: 'primary.main',
              '&:hover': { bgcolor: 'background.paper', borderColor: 'primary.main' }
            }}
          >
            {drawerOpen ? <IconChevronLeft style={{ fontSize: '1.5rem' }} /> : <IconChevronRight style={{ fontSize: '1.5rem' }} />}
          </IconButton>
        </Box>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(!drawerOpen)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: drawerOpen ? 'block' : 'none', lg: 'none' } }}
          slotProps={{
            paper: {
              sx: {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                borderRight: '1px solid',
                borderRightColor: 'divider',
                boxShadow: 'inherit'
              }
            }
          }}
        >
          {drawerHeader}
          {drawerContent}
          {drawerFooter}
        </Drawer>
      )}
    </Box>
  );
}

MainDrawer.propTypes = { window: PropTypes.func };
